import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFacility } from '../context/FacilityContext';
import customersService from '../services/customersService';
import unitsService from '../services/unitsService';
import reservationsService from '../services/reservationsService';
import PageBackLink from '../components/PageBackLink';

/**
 * Reserve an Available unit for a known customer or new lead. On submit:
 *   - reservations row created (status=active)
 *   - unit.status flipped to Reserved
 *   - No emails sent — operator follows up manually
 */

const labelCls = 'block text-[12px] font-medium text-slate-700 mb-1';
const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500';

const ReservationNew = () => {
  const { facilityId } = useFacility();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [units, setUnits] = useState([]);
  const [recipientMode, setRecipientMode] = useState('lead');
  const [customerId, setCustomerId] = useState('');
  const [leadName, setLeadName] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [unitId, setUnitId] = useState('');
  const [reservationDays, setReservationDays] = useState('7');
  const [feeAmount, setFeeAmount] = useState('0');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!facilityId) return;
    Promise.all([
      customersService.getAll(facilityId, { status: 'all', limit: 5000 }),
      unitsService.getAll(facilityId, { status_filter: 'Available' }),
    ])
      .then(([cRes, uRes]) => {
        setCustomers(cRes.data || []);
        setUnits(uRes.data || []);
      })
      .catch(() => {});
  }, [facilityId]);

  const availableUnits = useMemo(
    () => units.filter((u) => u.status === 'Available'),
    [units],
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!unitId) {
      setError('Pick a unit to reserve.');
      return;
    }
    if (recipientMode === 'existing' && !customerId) {
      setError('Pick a customer.');
      return;
    }
    if (recipientMode === 'lead' && !leadName && !leadEmail && !leadPhone) {
      setError('Enter at least one of name, email, or phone.');
      return;
    }

    const body = {
      unit_id: unitId,
      reservation_days: Number(reservationDays || 7),
      fee_amount: Number(feeAmount || 0),
      notes: notes || null,
    };
    if (recipientMode === 'existing') {
      body.customer_id = customerId;
    } else {
      body.lead_name = leadName || null;
      body.lead_email = leadEmail || null;
      body.lead_phone = leadPhone || null;
    }

    setSubmitting(true);
    try {
      await reservationsService.create(facilityId, body);
      // Leads is the landing tab now — return to the Reservations one.
      navigate('/reservations?tab=reservations');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not create reservation.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!facilityId) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h2 className="text-lg font-semibold text-slate-900">No facility selected</h2>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <PageBackLink to="/reservations?tab=reservations" label="Back to Reservations" />

      <div className="mb-5">
        <h1 className="text-xl font-semibold text-slate-900">New Reservation</h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          Hold an Available unit for a customer or lead.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        className="rounded-xl border border-slate-200/80 bg-white p-5 space-y-4"
      >
        <div>
          <label className={labelCls}>Recipient</label>
          <div className="flex gap-2 mb-3">
            {[
              { value: 'lead', label: 'New lead' },
              { value: 'existing', label: 'Existing customer' },
            ].map((o) => (
              <button
                type="button"
                key={o.value}
                onClick={() => setRecipientMode(o.value)}
                className={`text-[12px] font-medium rounded-full px-3 py-1 border transition-colors ${
                  recipientMode === o.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          {recipientMode === 'existing' ? (
            <select
              className={inputCls}
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
            >
              <option value="">— pick a customer —</option>
              {customers.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.full_name || c.email || c.phone}
                </option>
              ))}
            </select>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input
                className={inputCls}
                placeholder="Name"
                value={leadName}
                onChange={(e) => setLeadName(e.target.value)}
              />
              <input
                className={inputCls}
                placeholder="Email"
                type="email"
                value={leadEmail}
                onChange={(e) => setLeadEmail(e.target.value)}
              />
              <input
                className={inputCls}
                placeholder="Phone"
                value={leadPhone}
                onChange={(e) => setLeadPhone(e.target.value)}
              />
            </div>
          )}
        </div>

        <div>
          <label className={labelCls}>Unit</label>
          <select
            className={inputCls}
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
          >
            <option value="">
              — pick an available unit ({availableUnits.length}) —
            </option>
            {availableUnits.map((u) => (
              <option key={u._id} value={u._id}>
                {u.unit_number}
                {u.unit_type ? ` · ${u.unit_type}` : ''}
                {u.monthly_rate != null ? ` · $${Number(u.monthly_rate).toFixed(2)}/mo` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Hold length (days)</label>
            <input
              type="number"
              min="1"
              max="120"
              className={inputCls}
              value={reservationDays}
              onChange={(e) => setReservationDays(e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Reservation fee ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className={inputCls}
              value={feeAmount}
              onChange={(e) => setFeeAmount(e.target.value)}
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Tracked for bookkeeping. Charging this fee is a separate
              operator action.
            </p>
          </div>
        </div>

        <div>
          <label className={labelCls}>Notes (optional)</label>
          <textarea
            className={inputCls}
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        {error && <p className="text-[13px] text-red-600">{error}</p>}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-blue-600 text-white text-[13px] font-medium px-5 py-2 hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Reserving…' : 'Reserve unit'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/reservations?tab=reservations')}
            className="text-[13px] text-slate-500 hover:text-slate-700"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReservationNew;
